import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Heart, Users, Clock, MapPin, Share2, Calendar, ExternalLink, ShieldCheck, Copy, Check, HandHeart, UserPlus, UserCheck, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import GlassBoxBadge from '@/components/GlassBoxBadge';
import GlassBoxWidget from '@/components/GlassBoxWidget';
import DonationModal from '@/components/DonationModal';
import { getStore, setStore } from '@/utils/localStore';

const mockSupporters = [
  { name: 'Sarah Chen', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=0', amount: '$500' },
  { name: 'Michael Rivera', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=3', amount: '$250' },
  { name: 'Jessica Okafor', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=7', amount: '$100' },
  { name: 'David Park', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=12', amount: '$750' },
  { name: 'Emily Watson', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=15', amount: '$200' },
  { name: 'James Wright', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=10', amount: '$1,200' },
];

const mockActivityFeed = [
  { id: 1, type: 'donation', user: 'Sarah Chen', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=0', text: 'donated $500', time: '2 hours ago' },
  { id: 2, type: 'milestone', user: 'Project Team', avatar: null, text: 'reached 75% funding milestone', time: '5 hours ago' },
  { id: 3, type: 'comment', user: 'Michael Rivera', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=3', text: 'left an encouraging comment', time: '8 hours ago' },
  { id: 4, type: 'donation', user: 'David Park', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=12', text: 'donated $750', time: '1 day ago' },
  { id: 5, type: 'share', user: 'Emily Watson', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=15', text: 'shared this project', time: '2 days ago' },
];

function FollowButton({ projectSlug }) {
  const [state, setState] = useState('idle');
  const handleClick = () => {
    const newState = state === 'idle' ? 'following' : 'idle';
    setState(newState);
    toast.success(newState === 'following' ? 'You are now following this project!' : 'Unfollowed project');
    const follows = getStore('follows');
    const followerId = 'sarah-chen';
    const followingId = projectSlug || 'project';
    const idx = follows.findIndex(f => f.followerId === followerId && f.followingId === followingId && f.followingType === 'project');
    if (idx >= 0) {
      follows.splice(idx, 1);
    } else {
      follows.push({ followerId, followingId, followingType: 'project', createdAt: new Date().toISOString() });
    }
    setStore('follows', follows);
  };
  return (
    <Button
      onClick={handleClick}
      variant={state === 'following' ? 'outline' : 'default'}
      className={`rounded-full h-9 text-sm gap-1.5 ${state === 'following' ? 'border-[#7C3AED] text-[#7C3AED]' : 'bg-[#7C3AED] text-white hover:bg-[#6D28D9]'}`}
      data-testid="follow-project-btn"
    >
      {state === 'following' ? <><UserCheck className="w-3.5 h-3.5" /> Following</> : <><UserPlus className="w-3.5 h-3.5" /> Follow</>}
    </Button>
  );
}

function SupportersSection() {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="supporters-section">
      <h3 className="font-heading font-semibold text-sm text-[#111827] mb-4">Recent Supporters</h3>
      <div className="space-y-3">
        {mockSupporters.slice(0, 5).map((s) => (
          <div key={s.name} className="flex items-center gap-3">
            <img src={s.avatar} alt={s.name} className="w-8 h-8 rounded-full" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#111827] truncate font-heading">{s.name}</p>
            </div>
            <span className="text-xs font-semibold text-[#7C3AED] font-heading">{s.amount}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-3 text-center font-body">+ {Math.floor(Math.random() * 30 + 20)} more supporters</p>
    </div>
  );
}

function ActivityFeed() {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="activity-feed">
      <h3 className="font-heading font-semibold text-sm text-[#111827] mb-4">Recent Activity</h3>
      <div className="space-y-3">
        {mockActivityFeed.map(a => (
          <div key={a.id} className="flex items-start gap-2.5">
            {a.avatar ? (
              <img src={a.avatar} alt="" className="w-6 h-6 rounded-full shrink-0 mt-0.5" />
            ) : (
              <div className="w-6 h-6 rounded-full bg-[#10B981]/10 flex items-center justify-center shrink-0 mt-0.5">
                <ShieldCheck className="w-3 h-3 text-[#10B981]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs text-[#4B5563] font-body">
                <span className="font-medium text-[#111827]">{a.user}</span> {a.text}
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">{a.time}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjectSidebar({ project, transactions, onDonate }) {
  const percent = Math.round((project.fundingRaised / project.fundingGoal) * 100);
  return (
    <div className="space-y-5" data-testid="project-sidebar">
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="font-semibold text-[#111827] font-heading text-lg">${project.fundingRaised?.toLocaleString()}</span>
            <span className="text-gray-400 font-body">of ${project.fundingGoal?.toLocaleString()}</span>
          </div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB] rounded-full transition-all duration-700" style={{ width: `${Math.min(percent, 100)}%` }} />
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-xs text-gray-500 font-body">{percent}% funded</span>
            <GlassBoxBadge txHash={project.txHash} />
          </div>
        </div>
        <div className="space-y-2">
          <Button onClick={onDonate} className="w-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white rounded-full h-11 font-medium border-0 hover:shadow-lg transition-all" data-testid="donate-btn">
            <Heart className="w-4 h-4 mr-2" /> Donate
          </Button>
          {project.volunteerOpportunities > 0 && (
            <Link to="/volunteer">
              <Button variant="outline" className="w-full rounded-full h-11 font-medium border-gray-200" data-testid="volunteer-btn">
                <HandHeart className="w-4 h-4 mr-2" /> Volunteer
              </Button>
            </Link>
          )}
        </div>
        <div className="flex justify-center gap-2 mt-4 pt-4 border-t border-gray-100">
          {['Twitter', 'Facebook', 'LinkedIn', 'Link'].map(p => (
            <button key={p} className="w-9 h-9 rounded-full bg-gray-50 hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors" data-testid={`share-${p.toLowerCase()}`}>
              <Share2 className="w-3.5 h-3.5" />
            </button>
          ))}
        </div>
      </div>
      <SupportersSection />
      <ActivityFeed />
      <GlassBoxWidget transactions={transactions} />
    </div>
  );
}

function OverviewTab({ project }) {
  return (
    <div className="space-y-8" data-testid="tab-overview">
      <div>
        <h3 className="font-heading font-semibold text-lg text-[#111827] mb-4">About This Project</h3>
        {project.description?.split('\n').filter(Boolean).map((p, i) => (
          <p key={`desc-${i}`} className="text-[#4B5563] leading-relaxed mb-4 font-body">{p}</p>
        ))}
      </div>
      {project.photoGallery?.length > 0 && (
        <div>
          <h3 className="font-heading font-semibold text-lg text-[#111827] mb-4">Photo Gallery</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {project.photoGallery.map((img, i) => (
              <div key={img} className="aspect-[4/3] rounded-xl overflow-hidden">
                <img src={img} alt={`Project photo ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="bg-[#F3F4F6] rounded-2xl p-6">
        <h3 className="font-heading font-semibold text-lg text-[#111827] mb-2">Beneficiary Information</h3>
        <p className="text-[#4B5563] font-body leading-relaxed">
          {project.beneficiariesServed?.toLocaleString()} families and individuals supported in {project.location}. To protect the privacy and dignity of beneficiaries, individual information is not disclosed. All impact metrics are verified on-chain.
        </p>
      </div>
    </div>
  );
}

function ImpactTab({ project }) {
  const milestones = project.milestones || [];
  const completed = milestones.filter(m => m.completed).length;
  const metrics = [
    { label: 'Total Funds Received', value: `$${project.fundingRaised?.toLocaleString()}`, txHash: project.txHash },
    { label: 'Milestones Completed', value: `${completed} of ${milestones.length}`, txHash: project.txHash },
    { label: 'Beneficiaries Reached', value: project.beneficiariesServed?.toLocaleString(), txHash: project.txHash },
    { label: 'Volunteer Positions', value: project.volunteerOpportunities, txHash: project.txHash },
  ];
  return (
    <div className="space-y-8" data-testid="tab-impact">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((m) => (
          <div key={m.label} className="bg-white rounded-xl p-4 shadow-[0_4px_15px_rgb(0,0,0,0.03)] border border-gray-100">
            <p className="text-2xl font-heading font-semibold text-[#111827] mb-1">{m.value}</p>
            <div className="flex items-center gap-2">
              <p className="text-xs text-gray-500 font-body">{m.label}</p>
              <GlassBoxBadge txHash={m.txHash} />
            </div>
          </div>
        ))}
      </div>
      <div>
        <h3 className="font-heading font-semibold text-lg text-[#111827] mb-4">Milestone Timeline</h3>
        <div className="space-y-0">
          {milestones.map((m, i) => (
            <div key={m.title} className="flex gap-4 pb-6 last:pb-0">
              <div className="flex flex-col items-center">
                <div className={`w-4 h-4 rounded-full shrink-0 ${m.completed ? 'bg-[#10B981]' : 'bg-gray-200'}`} />
                {i < milestones.length - 1 && <div className="w-0.5 flex-1 bg-gray-100 mt-1" />}
              </div>
              <div className="pb-2">
                <p className="font-heading font-medium text-[#111827]">{m.title}</p>
                <p className="text-xs text-gray-500 font-body">{m.date}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="text-center">
        <a href="https://humadashportal.com" target="_blank" rel="noopener noreferrer" className="text-sm text-[#7C3AED] hover:text-[#2563EB] font-medium transition-colors font-body inline-flex items-center gap-1" data-testid="humadash-link">
          Data verified via HumaDash <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
}

function UpdatesTab({ project }) {
  const updates = project.updates || [];
  return (
    <div className="space-y-6" data-testid="tab-updates">
      {updates.length === 0 ? (
        <p className="text-gray-400 text-center py-8 font-body">No updates yet.</p>
      ) : updates.map((update) => (
        <div key={update.date + (update.title || '')} className="bg-white rounded-xl p-5 shadow-[0_4px_15px_rgb(0,0,0,0.03)] border border-gray-100">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-xs text-gray-500 font-body flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {update.date}
            </span>
            {update.type && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                update.type === 'milestone' ? 'bg-[#10B981]/10 text-[#10B981]' :
                update.type === 'disbursement' ? 'bg-[#2563EB]/10 text-[#2563EB]' :
                'bg-gray-100 text-gray-600'
              }`}>{update.type}</span>
            )}
            {update.txHash && <GlassBoxBadge label="On-chain" txHash={update.txHash} />}
          </div>
          {update.title && <h4 className="font-heading font-semibold text-[#111827] mb-2">{update.title}</h4>}
          <p className="text-sm text-[#4B5563] leading-relaxed font-body">{update.text}</p>
          {update.photo && (
            <img src={update.photo} alt="Update" className="mt-3 rounded-xl w-full h-48 object-cover" loading="lazy" />
          )}
        </div>
      ))}
    </div>
  );
}

function TransparencyTab({ transactions }) {
  const [copiedIdx, setCopiedIdx] = useState(null);
  const copyHash = (hash, idx) => {
    navigator.clipboard.writeText(hash);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };
  const sorted = [...transactions].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  return (
    <div data-testid="tab-transparency">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="w-5 h-5 text-[#10B981]" />
        <h3 className="font-heading font-semibold text-lg text-[#111827]">On-Chain Ledger</h3>
      </div>
      <p className="text-sm text-[#4B5563] mb-6 font-body">Every transaction for this project, verified on the blockchain. Click any hash to view on BscScan.</p>
      {sorted.length === 0 ? (
        <p className="text-gray-400 text-center py-8 font-body">No transactions recorded yet.</p>
      ) : (
        <div className="bg-white rounded-xl shadow-[0_4px_15px_rgb(0,0,0,0.03)] border border-gray-100 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50/50">
                <TableHead className="font-heading text-xs">Date</TableHead>
                <TableHead className="font-heading text-xs">Description</TableHead>
                <TableHead className="font-heading text-xs text-right">Amount</TableHead>
                <TableHead className="font-heading text-xs">Tx Hash</TableHead>
                <TableHead className="font-heading text-xs w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((tx, i) => (
                <TableRow key={tx.id || i}>
                  <TableCell className="text-xs text-gray-500 font-body whitespace-nowrap">
                    {new Date(tx.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </TableCell>
                  <TableCell className="text-sm font-body">
                    {tx.type === 'donation' ? 'Donation received' : tx.type === 'grant' ? 'Grant received' : 'Corporate match'}
                    {tx.donorName && <span className="text-gray-400"> from {tx.donorName}</span>}
                  </TableCell>
                  <TableCell className="text-sm font-semibold text-right font-heading">${tx.amount?.toLocaleString()}</TableCell>
                  <TableCell>
                    <code className="text-xs text-[#7C3AED] font-mono">{tx.txHash?.slice(0, 10)}...</code>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <button onClick={() => copyHash(tx.txHash, i)} className="text-gray-400 hover:text-[#111827] transition-colors" data-testid={`copy-tx-${i}`}>
                        {copiedIdx === i ? <Check className="w-3.5 h-3.5 text-[#10B981]" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                      <a href={`https://bscscan.com/tx/${tx.txHash}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#7C3AED] transition-colors" data-testid={`view-tx-${i}`}>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function NGOTab({ project, ngo }) {
  if (!ngo) return <p className="text-gray-400 text-center py-8 font-body">Loading NGO information...</p>;
  const badges = ['501(c)(3) Verified', 'HumaDash Integrated', 'KYB Complete'];
  return (
    <div data-testid="tab-ngo">
      <div className="bg-white rounded-xl p-6 shadow-[0_4px_15px_rgb(0,0,0,0.03)] border border-gray-100">
        <div className="flex items-start gap-4 mb-4">
          <Avatar className="w-14 h-14">
            <AvatarFallback className="bg-gradient-to-br from-[#7C3AED] to-[#2563EB] text-white font-heading font-semibold text-lg">
              {ngo.name?.split(' ').map(w => w[0]).join('').slice(0, 2)}
            </AvatarFallback>
          </Avatar>
          <div>
            <h3 className="font-heading font-semibold text-lg text-[#111827]">{ngo.name}</h3>
            <p className="text-sm text-gray-500 font-body flex items-center gap-1"><MapPin className="w-3 h-3" /> {ngo.location}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {badges.map(b => (
            <span key={b} className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] text-xs font-medium">
              <ShieldCheck className="w-3 h-3" /> {b}
            </span>
          ))}
        </div>
        <p className="text-sm text-[#4B5563] leading-relaxed mb-4 font-body">{ngo.description}</p>
        <div className="grid grid-cols-3 gap-4 py-4 border-t border-gray-100">
          <div className="text-center">
            <p className="text-lg font-heading font-semibold text-[#111827]">${ngo.totalFunding?.toLocaleString()}</p>
            <p className="text-xs text-gray-500 font-body">Total Raised</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-heading font-semibold text-[#111827]">{ngo.projectsCount}</p>
            <p className="text-xs text-gray-500 font-body">Projects</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-heading font-semibold text-[#111827]">{ngo.yearFounded}</p>
            <p className="text-xs text-gray-500 font-body">Founded</p>
          </div>
        </div>
        <Link to={`/ngo/${ngo.slug}`}>
          <Button variant="outline" className="w-full rounded-full mt-4 border-gray-200 font-body" data-testid="view-ngo-profile">
            View Full Profile
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [ngo, setNgo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [donateOpen, setDonateOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [projects, txAll] = await Promise.all([
          fetch('/data/projects.json').then(r => r.json()),
          fetch('/data/transactions.json').then(r => r.json()),
        ]);
        const found = projects.find(p => p.slug === slug);
        if (!found) {
          setProject({ error: 'Project not found' });
          setLoading(false);
          return;
        }
        const projectTx = txAll.filter(t => t.projectId === found.id);
        setProject({ ...found, transactions: projectTx });
        setTransactions(projectTx);
        if (found.ngoId) {
          const ngos = await fetch('/data/ngos.json').then(r => r.json());
          const foundNgo = ngos.find(n => n.id === found.ngoId);
          if (foundNgo) setNgo(foundNgo);
        }
      } catch (err) {
        toast.error('Failed to load project details');
      } finally { setLoading(false); }
    };
    fetchData();
  }, [slug]);

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 py-20 text-center">
      <div className="animate-pulse space-y-4">
        <div className="h-64 bg-gray-100 rounded-2xl" />
        <div className="h-8 bg-gray-100 rounded w-2/3 mx-auto" />
        <div className="h-4 bg-gray-100 rounded w-1/3 mx-auto" />
      </div>
    </div>
  );

  if (!project || project.error) return (
    <div className="max-w-7xl mx-auto px-4 py-20 text-center">
      <h2 className="font-heading text-2xl font-semibold text-[#111827] mb-2">Project not found</h2>
      <Link to="/explore"><Button variant="outline" className="rounded-full">Back to Explore</Button></Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="project-detail-page">
      <div className="relative h-48 sm:h-64 md:h-80 overflow-hidden">
        <img src={project.heroImage} alt={project.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <Link to="/explore">
              <Button variant="ghost" className="text-white/80 hover:text-white mb-3 -ml-2 text-sm" data-testid="back-to-explore">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to Explore
              </Button>
            </Link>
            <div className="flex items-center gap-2 mb-2">
              <Link to={`/ngo/${ngo?.slug || ''}`} className="text-white/80 hover:text-white text-sm font-body transition-colors" data-testid="project-ngo-link">
                {project.ngoName}
              </Link>
              <GlassBoxBadge txHash={project.txHash} />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h1 className="text-2xl md:text-4xl font-heading font-semibold text-white tracking-tight">{project.title}</h1>
              <FollowButton projectSlug={slug} />
            </div>
            <div className="flex items-center gap-4 mt-2 text-white/70 text-sm font-body">
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {project.location}</span>
              <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {project.beneficiariesServed?.toLocaleString()} served</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:hidden">
            <ProjectSidebar project={project} transactions={transactions} onDonate={() => setDonateOpen(true)} />
          </div>
          <div className="flex-1 min-w-0">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="w-full justify-start bg-white rounded-xl p-1 shadow-[0_4px_15px_rgb(0,0,0,0.03)] mb-6 h-auto overflow-x-auto scrollbar-hide flex-nowrap" data-testid="project-tabs">
                {['Overview', 'Impact', 'Updates', 'Transparency', 'NGO'].map(tab => (
                  <TabsTrigger key={tab} value={tab.toLowerCase()} className="rounded-lg px-4 py-2 text-sm font-body data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#7C3AED] data-[state=active]:to-[#2563EB] data-[state=active]:text-white" data-testid={`tab-${tab.toLowerCase()}`}>
                    {tab}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="overview"><OverviewTab project={project} /></TabsContent>
              <TabsContent value="impact"><ImpactTab project={project} /></TabsContent>
              <TabsContent value="updates"><UpdatesTab project={project} /></TabsContent>
              <TabsContent value="transparency"><TransparencyTab transactions={transactions} /></TabsContent>
              <TabsContent value="ngo"><NGOTab project={project} ngo={ngo} /></TabsContent>
            </Tabs>
          </div>
          <div className="hidden lg:block w-80 shrink-0">
            <div className="sticky top-24">
              <ProjectSidebar project={project} transactions={transactions} onDonate={() => setDonateOpen(true)} />
            </div>
          </div>
        </div>
      </div>

      <DonationModal open={donateOpen} onOpenChange={setDonateOpen} projectTitle={project.title} ngoName={project.ngoName} />
    </div>
  );
}
